import axios, { AxiosProgressEvent } from 'axios';
import {
  FmeaComponentNodes,
  FmeaComponentNodesInput,
  FmeaFailureDetail,
  FmeaFailureDetailCreateInput,
  FmeaFailureDetailDeleteInput,
  FmeaFailureDetailUpdateInput,
  FmeaFailureMode,
  FmeaFailureModeCreateInput,
  FmeaFailureModeDeleteInput,
  FmeaFailureModeGenerationRun,
  FmeaFailureModeUpdateInput,
  FmeaImportedComponentNodes,
  FmeaProject,
  FmeaProjectCreateInput,
  FmeaProjectRiskCriteriaInput,
  FmeaProjectRiskRank,
  FmeaProjectTag,
  FmeaSheetCorrectiveAction,
  FmeaSheetCorrectiveActionCreateInput,
  FmeaSheetCorrectiveActionDeleteInput,
  FmeaSheetCorrectiveActionUpdateInput,
  FmeaSystem,
  FmeaSystemCreateInput,
  FmeaSystemResource,
  FmeaSystemResourceRevisionContent,
} from '@/types/fmea';

export const createFmeaProject = async (input: FmeaProjectCreateInput) => {
  const { data } = await axios.post<FmeaProject>('/fmea/projects', input);
  return data;
};

export const updateFmeaProject = async (
  id: number,
  input: FmeaProjectCreateInput,
) => {
  const { data } = await axios.put<FmeaProject>(`/fmea/projects/${id}`, input);
  return data;
};

export const deleteFmeaProject = async (id: number) => {
  await axios.delete(`/fmea/projects/${id}`);
};

export const createFmeaProjectTag = async (name: string) => {
  const { data } = await axios.post<FmeaProjectTag>('/fmea/project_tags', {
    name,
  });
  return data;
};

export const createFmeaSystem = async (
  projectId: number,
  input: FmeaSystemCreateInput,
) => {
  const { data } = await axios.post<FmeaSystem>(
    `/fmea/projects/${projectId}/systems`,
    input,
  );
  return data;
};

export const updateFmeaSystemSupplementaryText = async (
  systemId: number,
  supplementaryText: string,
) => {
  const { data } = await axios.put<FmeaSystem>(
    `/fmea/systems/${systemId}/supplementary_text`,
    { supplementary_text: supplementaryText },
  );
  return data;
};

export type FmeaComponentNodesErrorResponse = {
  error: string;
  error_type?: 'failure_modes_exist';
};

export const replaceFmeaComponentNodes = async (
  systemId: number,
  input: FmeaComponentNodesInput,
) => {
  const { data } = await axios.put<FmeaComponentNodes>(
    `/fmea/systems/${systemId}/component_nodes`,
    input,
  );
  return data;
};

export const importFmeaComponentNodes = async (
  systemId: number,
  file: File,
  onUploadProgress?: (loaded: number, total: number | undefined) => void,
) => {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await axios.post<FmeaImportedComponentNodes>(
    `/fmea/systems/${systemId}/component_nodes/import`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      // 既定の 35 秒ではアップロードと解析の両方を含めると遅い回線で足りない。
      timeout: 10 * 60 * 1000,
      onUploadProgress: onUploadProgress
        ? (event: AxiosProgressEvent) =>
            onUploadProgress(event.loaded, event.total)
        : undefined,
    },
  );
  return data;
};

export const listFmeaSystemResources = async (systemId: number) => {
  const { data } = await axios.get<FmeaSystemResource[]>(
    `/fmea/systems/${systemId}/resources`,
  );
  return data;
};

export const getFmeaSystemResourceRevisionContent = async (
  revisionId: number,
  signal?: AbortSignal,
) => {
  const { data } = await axios.get<FmeaSystemResourceRevisionContent>(
    `/fmea/resource_revisions/${revisionId}`,
    { signal },
  );
  return data;
};

export const createFmeaSystemResource = async (
  systemId: number,
  input: { name: string; signed_id: string },
) => {
  const { data } = await axios.post<FmeaSystemResource>(
    `/fmea/systems/${systemId}/resources`,
    input,
  );
  return data;
};

export const getFmeaComponentNodes = async (systemId: number) => {
  const { data } = await axios.get<FmeaComponentNodes>(
    `/fmea/systems/${systemId}/component_nodes`,
  );
  return data;
};

export const deleteFmeaSystemResource = async (resourceId: number) => {
  await axios.delete(`/fmea/resources/${resourceId}`);
};

export const retryFmeaSystemResourceExtraction = async (revisionId: number) => {
  const { data } = await axios.post<FmeaSystemResource>(
    `/fmea/resource_revisions/${revisionId}/extraction`,
  );
  return data;
};

export const startFmeaWorksheetGeneration = async (systemId: number) => {
  const { data } = await axios.post<FmeaFailureModeGenerationRun>(
    `/fmea/systems/${systemId}/failure_mode_generations`,
  );
  return data;
};

export const listFmeaProjectRiskCriteria = async (projectId: number) => {
  const { data } = await axios.get<FmeaProjectRiskRank[]>(
    `/fmea/projects/${projectId}/risk_criteria`,
  );
  return data;
};

export const replaceFmeaProjectRiskCriteria = async (
  projectId: number,
  input: FmeaProjectRiskCriteriaInput,
) => {
  const { data } = await axios.put<FmeaProjectRiskRank[]>(
    `/fmea/projects/${projectId}/risk_criteria`,
    input,
  );
  return data;
};

export const updateFmeaFailureDetail = async (
  id: number,
  input: FmeaFailureDetailUpdateInput,
) => {
  const { data } = await axios.patch<FmeaFailureDetail>(
    `/fmea/sheet_failure_details/${id}`,
    input,
  );
  return data;
};

export const deleteFmeaFailureDetail = async (
  id: number,
  input: FmeaFailureDetailDeleteInput,
) => {
  await axios.delete(`/fmea/sheet_failure_details/${id}`, { data: input });
};

export const createFmeaFailureDetail = async (
  failureModeId: number,
  input: FmeaFailureDetailCreateInput,
) => {
  const { data } = await axios.post<FmeaFailureDetail>(
    `/fmea/sheet_failure_modes/${failureModeId}/sheet_failure_details`,
    input,
  );
  return data;
};

export const createFmeaFailureMode = async (
  systemId: number,
  input: FmeaFailureModeCreateInput,
) => {
  const { data } = await axios.post<FmeaFailureMode>(
    `/fmea/systems/${systemId}/sheet_failure_modes`,
    input,
  );
  return data;
};

export const updateFmeaFailureMode = async (
  id: number,
  input: FmeaFailureModeUpdateInput,
) => {
  const { data } = await axios.patch<FmeaFailureMode>(
    `/fmea/sheet_failure_modes/${id}`,
    input,
  );
  return data;
};

export const deleteFmeaFailureMode = async (
  id: number,
  input: FmeaFailureModeDeleteInput,
) => {
  await axios.delete(`/fmea/sheet_failure_modes/${id}`, { data: input });
};

export const updateFmeaSheetCorrectiveAction = async (
  id: number,
  input: FmeaSheetCorrectiveActionUpdateInput,
) => {
  const { data } = await axios.patch<FmeaSheetCorrectiveAction>(
    `/fmea/sheet_corrective_actions/${id}`,
    input,
  );
  return data;
};

export const createFmeaSheetCorrectiveAction = async (
  failureDetailId: number,
  input: FmeaSheetCorrectiveActionCreateInput,
) => {
  const { data } = await axios.post<FmeaSheetCorrectiveAction>(
    `/fmea/sheet_failure_details/${failureDetailId}/sheet_corrective_actions`,
    input,
  );
  return data;
};

export const deleteFmeaSheetCorrectiveAction = async (
  id: number,
  input: FmeaSheetCorrectiveActionDeleteInput,
) => {
  await axios.delete(`/fmea/sheet_corrective_actions/${id}`, { data: input });
};
